import { IFeature } from '../../components/product-filters-menu/interfaces/feature.interface';
import { FeatureFilter } from '../../components/product-filters-menu/models/feature-filter.model';
import { ProductFeatureDetailsDto } from '../models/product-feature-details-dto.model';
import { SchemaValueFields } from '../models/schema-value-fielts.model';

// Sentinel added to an enum filter's `values` alongside real options (see OrderingDropdownFilter) to let the
// customer explicitly opt in/out of seeing variants that have no value at all for that feature.
export const NOT_CONFIGURED_FILTER_VALUE = '__NOT_CONFIGURED__';

// True when this product actually carries a value for the feature - as opposed to no detail at all, or a
// detail present but empty inside (e.g. EnumValues: [] rather than null/absent).
export const hasUsableFeatureValue = (value: SchemaValueFields | undefined | null): boolean => {
    if (!value) {
        return false;
    }
    return (
        !!value.enumValue ||
        (Array.isArray(value.enumValues) && value.enumValues.length > 0) ||
        !!value.textValue ||
        typeof value.numericValue === 'number' ||
        typeof value.booleanValue === 'boolean' ||
        (typeof value.numericRangeFrom === 'number' && typeof value.numericRangeTo === 'number')
    );
};

const matchesEnumFilter = (value: SchemaValueFields, filter: FeatureFilter): boolean => {
    const allowedValues = filter.values;
    if (!allowedValues || allowedValues.length === 0) {
        return true;
    }
    if (value.enumValue) {
        return allowedValues.includes(value.enumValue);
    }
    if (value.enumValues && value.enumValues.length > 0) {
        return value.enumValues.some((enumValue) => allowedValues.includes(enumValue));
    }
    if (value.textValue) {
        return allowedValues.includes(value.textValue);
    }
    if (typeof value.numericValue === 'number') {
        return allowedValues.includes(value.numericValue.toString());
    }
    return false;
};

const matchesBooleanFilter = (value: SchemaValueFields, filter: FeatureFilter): boolean => {
    if (typeof filter.booleanValue !== 'boolean') {
        return true;
    }
    return value.booleanValue === filter.booleanValue;
};

const matchesRangeFilter = (value: SchemaValueFields, filter: FeatureFilter): boolean => {
    const from = parseFloat(filter.rangeFrom ?? '');
    const to = parseFloat(filter.rangeTo ?? '');
    if (Number.isNaN(from) || Number.isNaN(to)) {
        return true;
    }
    if (typeof value.numericRangeFrom === 'number' && typeof value.numericRangeTo === 'number') {
        return value.numericRangeFrom <= to && value.numericRangeTo >= from;
    }
    if (typeof value.numericValue === 'number') {
        return value.numericValue >= from && value.numericValue <= to;
    }
    if (value.textValue) {
        const parsed = parseFloat(value.textValue.replace(',', '.'));
        if (!Number.isNaN(parsed)) {
            return parsed >= from && parsed <= to;
        }
    }
    return false;
};

const matchesSingleFilter = (
    productFeatureDetails: ProductFeatureDetailsDto[],
    filter: FeatureFilter,
    availableFeatures: IFeature[]
): boolean => {
    const feature = availableFeatures.find((f) => f.id === filter.featureId);
    if (!feature) {
        return true;
    }
    const detail = productFeatureDetails.find((d) => d.featureCode === feature.code);
    if (!detail || !hasUsableFeatureValue(detail.value)) {
        // No usable value for this feature on this product. Enum filters have an explicit "Not configured"
        // checkbox (see OrderingDropdownFilter) for the customer to opt in/out of seeing these - respect it.
        // Boolean/range filters have no such affordance, so missing data there never excludes the product.
        if (filter.valueType === 'enum') {
            return !!filter.values?.includes(NOT_CONFIGURED_FILTER_VALUE);
        }
        return true;
    }

    switch (filter.valueType) {
        case 'enum':
            return matchesEnumFilter(detail.value, filter);
        case 'boolean':
            return matchesBooleanFilter(detail.value, filter);
        case 'range':
            return matchesRangeFilter(detail.value, filter);
        default:
            return true;
    }
};

export const productMatchesFeatureFilters = (
    productFeatureDetails: ProductFeatureDetailsDto[] | undefined,
    featuresFilters: FeatureFilter[],
    availableFeatures: IFeature[]
): boolean => {
    if (!featuresFilters || featuresFilters.length === 0) {
        return true;
    }
    // A product with no feature data at all is no longer blanket-excluded - each filter below already
    // treats a missing detail as non-exclusionary, so an empty/undefined list just falls through the same way.
    return featuresFilters.every((filter) =>
        matchesSingleFilter(productFeatureDetails ?? [], filter, availableFeatures)
    );
};
